import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Button from "components/atom/button/Button.tsx";
import LangAndPolicies from "components/layouts/LangAndPolicies.tsx";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";
import { Form, Formik } from "formik";
import Input from "components/atom/Input.tsx";
import computerPng from "assets/images/computer.png";
import phonePng from "assets/images/phone.png";
import logoPng from "assets/images/logo/logo.png";
import cityLongBgPng from "assets/images/city-long-bg.png";
import cityBgPng from "assets/images/city-bg.png";
import arrowHomepagePng from "assets/images/arrow-homepage.png";


const HomePageContent = () => {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-white max-w-[1600px] mx-auto relative overflow-hidden">
        <div className="container mx-auto text-center relative pt-20 md:pt-24 pb-20">
          <div className="max-w-screen-md mx-auto flex flex-col justify-center items-center gap-12 relative z-2">
            <img alt="Bethere logo" src={logoPng} className="w-[240px] h-auto block object-center object-contain" />
            <h1 className="font-bold">
              {t("homePage.hero.title")}
            </h1>
            <Button
              to="/map"
              className=""
            >
              {t("homePage.hero.accessMapButton")}
            </Button>

          </div>
        </div>
        <div className="-bottom-24 md:-bottom-12 -left-12 md:left-0 -right-12 md:right-0 absolute z-0">
          <div className="relative">
            <img src={cityBgPng}
                 className="block md:hidden w-full h-auto object-cover object-bottom" />
            <img src={cityLongBgPng}
                 className="hidden md:block w-full h-auto object-cover object-bottom" />
            <div className="p-index__city-hover" />
            <div className="p-index__city-hover-2" />
          </div>
        </div>

      </section>

      {/* Users Section */}
      <section className="relative max-w-[2400px] mx-auto">
        <div className="w-1/3 lg:w-1/2 absolute left-0 bottom-0 top-0 ">
          <img src={phonePng} className="block h-full w-full object-contain object-left" />
        </div>
        <div
          className="absolute hidden lg:flex lg:w-1/2 left-0 pr-16 bottom-16 lg:bottom-40 xl:bottom-52 flex-row justify-end items-end">
          <img src={arrowHomepagePng}
               className="block h-full lg:w-[200px] xl:w-[240px] object-contain object-right-bottom" />
        </div>
        <div className="container mx-auto py-16 lg:py-40 xl:py-52">
          <div className="flex flex-row items-center justify-end gap-12 lg:gap-32">
            <div className="w-2/3 lg:w-1/2 flex flex-col items-start justify-center">
              <h2 className="text-2xl font-bold text-green-500 mb-1.5">
                {t("homePage.users.title")}
              </h2>
              <h3 className="text-2xl font-semibold text-black mb-6">
                {t("homePage.users.subtitle")}
              </h3>
              <p className=" text-lg mb-8 leading-relaxed">
                {t("homePage.users.description")}
              </p>
              <Button
                to="/map"
              >
                {t("homePage.users.startExploringButton")}
              </Button>
            </div>

          </div>
        </div>
      </section>

      {/* Organizations Section */}
      <section className="bg-gray-50 py-16 lg:py-0">
        <div className="container mx-auto">
          <div
            className="w-full md:w-11/12 mx-auto flex flex-col lg:flex-row-reverse justify-start lg:justify-center items-start lg:items-center gap-12 lg:gap-24">
            <div className="w-2/3 md:w-1/3 lg:mx-auto lg:w-1/2 flex flex-row justify-start lg:justify-end items-center">
              <img src={computerPng} className="block h-full w-full object-contain object-left lg:object-center" />
            </div>
            <div className="lg:w-1/2 lg:py-16 xl:py-24">
              <h2 className="text-2xl font-bold text-green-500 mb-1.5">
                {t("homePage.organizations.title")}
              </h2>
              <h3 className="text-2xl font-semibold text-black mb-6">
                {t("homePage.organizations.subtitle")}
              </h3>
              <p className="text-lg mb-8 leading-relaxed md:pr-24">
                {t("homePage.organizations.description")}
              </p>
              <Button
                to={{ pathname: location.pathname, hash: "#/auth/signup" }}
              >
                {t("homePage.organizations.startPublishingButton")}
              </Button>
            </div>


          </div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-16 border-t border-gray-200">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 text-2xl">📍</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t("homePage.features.geolocation.title")}</h3>
              <p className="text-gray-600">
                {t("homePage.features.geolocation.description")}
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-purple-600 text-2xl">🎉</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t("homePage.features.variety.title")}</h3>
              <p className="text-gray-600">
                {t("homePage.features.variety.description")}
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-green-600 text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t("homePage.features.realTime.title")}</h3>
              <p className="text-gray-600">
                {t("homePage.features.realTime.description")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-12">{t("homePage.testimonials.title")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <p className="text-gray-600 mb-4">
                "{t("homePage.testimonials.testimonial1")}"
              </p>
              <div className="text-yellow-500 mb-2">★★★★★</div>
              <p className="">{t("homePage.testimonials.user1")}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <p className="text-gray-600 mb-4">
                "{t("homePage.testimonials.testimonial2")}"
              </p>
              <div className="text-yellow-500 mb-2">★★★★★</div>
              <p className="">{t("homePage.testimonials.user2")}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <p className="text-gray-600 mb-4">
                "{t("homePage.testimonials.testimonial3")}"
              </p>
              <div className="text-yellow-500 mb-2">★★★★★</div>
              <p className="">{t("homePage.testimonials.user3")}</p>
            </div>
          </div>
          <Button
            className="mt-12"
            to="/map"
          >
            {t("homePage.testimonials.accessMapButton")}
          </Button>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-green-500 text-white py-16">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">{t("homePage.newsletter.title")}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white">
            {t("homePage.newsletter.description")}
          </p>
          <Formik
            initialValues={{
              newsletter_email: ""
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validate={values => {
              const errors: Partial<{ [K in keyof typeof values]: string }> = {};//same keys as type but with string value
              if (!values.newsletter_email) {
                errors.newsletter_email = t("forms.errors.email");
              }
              return errors;
            }}
            onSubmit={async (values, { setSubmitting, setStatus, setFieldError, setFieldValue }) => {
              setSubmitting(true);
              setStatus(null);

              const [res, err] = await tryAsyncFunc(ajax.post("/public/globals/subscribe", {
                email: values.newsletter_email
              }));
              setSubmitting(false);

              if (err) {
                const errData = err.response.data || {};
                const errors = errData.errors || {};

                if (errors.global) {
                  setStatus(errors.global);
                  delete errors.global;
                }

                Object.keys(errors).forEach(errKey => {
                  setFieldError(errKey, errors[errKey]);
                });
                return;
              }

              setFieldValue("newsletter_email", "");//reset
              setStatus(t("homePage.newsletter.success"));
            }}
          >
            {({ isSubmitting, status, errors }) => (
              <Form className="w-full">
                <div className="w-full max-w-md mx-auto">
                  <div className="w-full flex flex-row items-center justify-center gap-4">
                    <div className="flex-1">
                      <Input
                        noClassName
                        autoComplete="email"
                        name="newsletter_email"
                        type="email"
                        inputProps={{
                          className: "w-full h-[54px] px-6 focus:outline-none text-black rounded-lg border-none mt-0"
                        }}
                        className="w-full"
                        placeholder={t("homePage.newsletter.emailPlaceholder")}
                        error={errors.newsletter_email}
                      />
                    </div>

                    <Button style="white" type="submit" disabled={isSubmitting}>
                      {t("homePage.newsletter.signupButton")}
                    </Button>
                  </div>

                  <p className=" text-black mt-4">{status}</p>

                </div>
              </Form>
            )}
          </Formik>
        </div>
      </section>

      <div className="w-full mb-32">
        <LangAndPolicies showSocialLinks />
      </div>
    </div>
  );
};

export default HomePageContent;
